import type { SchoolTestApi } from '@mathflat/domain/@entities/(Content)/SchoolTest/api'
import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import { NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS } from '@mathflat/domain/@entities/Curriculum/constant'
import { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import curriculumApi from 'src/api/curriculum.api'
import { CurriculumTreeByChapter, MakeProblem, MakeProblemColl, SchoolGrade } from 'src/dto'
import CheckableMap from 'src/models/CheckableMap'

import { makeAdapter } from './Adapter'

class CurriculumController {
  adapter = {
    curriculumTreeByChapter: makeAdapter({
      from(curriculum: res.Curriculum[]) {
        return new CurriculumTreeByChapter(curriculum)
      },
    }),
    _curriculumCore: {
      from(data: CurriculumApi.Response.CurriculumCore) {
        return new CurriculumEntity.CurriculumCore(data)
      },
    },
    curriculumCore: {
      from(data: CurriculumApi.Response.CurriculumCore) {
        const { revisions, schoolTypes, semesters: semester, subjects: subject } = data

        const activeRevisions = revisions.filter(({ status }) => status === 'ACTIVE')
        const activeSchools = schoolTypes.filter(({ status }) => status === 'ACTIVE')

        const map = new Map<
          common.SchoolType,
          {
            id: number
            trieKey: string
            grade: common.GradeSubject
            semester: common.Semester | null
            revision: common.Revision
            orderingNumber: number
          }[]
        >()

        const activeSemesters = semester
          .filter(({ status }) => status === 'ACTIVE')
          .sort((a, b) => a.orderingNumber - b.orderingNumber)
        const activeSubjects = subject
          .filter(
            ({ status, trieKey }) =>
              status === 'ACTIVE' &&
              !Object.values(NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS).includes(trieKey),
          )
          .sort((a, b) => a.orderingNumber - b.orderingNumber)

        const elementarySemesters = activeSemesters
          .filter(({ schoolType }) => schoolType === 'ELEMENTARY')
          .map(({ grade, semester, ...params }) => ({
            ...params,
            semester: semester as common.Semester,
            grade: `${grade}` as common.GradeSubject,
          }))
        const middleSemesters = activeSemesters
          .filter(({ schoolType }) => schoolType === 'MIDDLE')
          .map(({ grade, semester, ...params }) => ({
            ...params,
            semester: semester as common.Semester,
            grade: `${grade}` as common.GradeSubject,
          }))
        const highSubjectes = activeSubjects
          .filter(({ schoolType }) => schoolType === 'HIGH')
          .map(({ subject, ...params }) => ({
            ...params,
            grade: subject as common.Subject_High,
            semester: null,
          }))

        map.set('ELEMENTARY', elementarySemesters)
        map.set('MIDDLE', middleSemesters)
        map.set('HIGH', highSubjectes)

        return {
          semesterSubjectMapBySchoolType: map,
          revisions: activeRevisions,
          schools: activeSchools,
        }
      },

      toSemesterSubjectArr(data: CurriculumApi.Response.CurriculumCore) {
        const { semesters: semester, subjects: subject } = data

        const _semester = semester
          .filter(({ status }) => status === 'ACTIVE')
          .map(({ id, schoolType, grade, revision, semester: value, orderingNumber, trieKey }) => ({
            curriculumId: id,
            type: schoolType,
            label:
              revision === 'CURRICULUM_15'
                ? `${SchoolGrade.getKoreanSchoolType(schoolType)}${grade}-${value}`
                : `${SchoolGrade.getKoreanSchoolType(schoolType)}${grade}-${value}(22개정)`,
            orderingNumber,
            curriculumKey: trieKey,
          }))
        const _subject = subject
          .filter(({ status }) => status === 'ACTIVE')
          .map(({ id, schoolType, subject: value, revision, orderingNumber, trieKey }) => ({
            curriculumId: id,
            type: schoolType,
            label:
              revision === 'CURRICULUM_15'
                ? `${SchoolGrade.getKoreanHighSubjectShort(value)}`
                : `${SchoolGrade.getKoreanHighSubjectShort(value)}(22개정)`,
            orderingNumber,
            curriculumKey: trieKey,
          }))

        return [..._semester, ..._subject].sort((a, b) => a.orderingNumber - b.orderingNumber)
      },
    },

    curriculumIds: makeAdapter({
      from(response: CurriculumApi.Response.CurriculumInfo) {
        const { id, name, url } = response

        return new MakeProblem({
          conceptName: name,
          id,
          curriculumId: id || -1,
          conceptId: id,
          problemImageUrl: url || '',
          itemType: 'MIDDLE_CHAPTER_CONCEPT',
        })
      },
    }),
  }
  fetchCurriculumCore = async () => {
    const {
      data: { data },
    } = await curriculumApi.getCurriculumCore()
    return this.adapter._curriculumCore.from(data)
  }

  // 즐겨찾는 문제 필터링을 위한 교육과정 트리 가져오기
  fetchCurriculumIdTree = async () => {
    const {
      data: { data },
    } = await curriculumApi.getCurriculumCore()

    return this.adapter.curriculumCore.toSemesterSubjectArr(data)
  }

  /**
   * @deprecated
   */
  _fetchCurriculumsFromIds = async (
    curriculumIds: number[],
    type: 'LITTLE_CHAPTER' | 'CONCEPT',
  ) => {
    const {
      data: { data },
    } = await curriculumApi.getCurriculum({
      idSet: curriculumIds,
      type,
    })
    const list = data.filter(({ url }) => url).map(this.adapter.curriculumIds.from)
    return new MakeProblemColl(CheckableMap.fromArr(list, 'id'))
  }

  fetchCurriculumsFromIdsV2 = async (
    curriculumIds: number[],
    type: 'LITTLE_CHAPTER' | 'CONCEPT',
  ) => {
    const {
      data: { data },
    } = await curriculumApi.getCurriculum({
      idSet: curriculumIds,
      type,
    })
    const list = data.filter(({ url }) => url)
    return list
  }

  /* 커리큘럼 트리 */
  fetchCurriculumByTrieKey = async (trieKey: string) => {
    const { data } = await curriculumApi.getCurriculumTreeByTrieKey(trieKey)
    return data
  }

  fetchCurriculumTreeByChapter = async (depth = 1) => {
    const {
      data: { data },
    } = await curriculumApi.getCurriculumTreeGroups(depth)
    return this.adapter.curriculumTreeByChapter.from(data)
  }

  fetchTopicAndSubTopicList = async (conceptId: number) => {
    const { data } = await curriculumApi.getTopicAndSubTopic(conceptId)
    return data
  }

  fetchCurriculumsBySchoolTestIds = async (
    params: SchoolTestApi.Request.SchoolTestGroupBySchool,
  ) => {
    const { data } = await curriculumApi.getCurriculumsBySchoolTestIds(params)
    return data
  }
}

export const curriculumController = new CurriculumController()
